/**
 * Brand Analytics Component
 * Real engagement data: product views, brand views, saves, traffic sources
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar
} from 'recharts';
import { 
  Eye, Users, Target, Trophy, Clock,
  Activity, BarChart3
} from 'lucide-react';
import { useBrand } from '../../context/BrandContext';

const PERIOD_OPTIONS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

export function BrandAnalytics() {
  const { authFetch, isAuthenticated } = useBrand();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [analytics, setAnalytics] = useState(null);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const analyticsRes = await authFetch(`/api/brand/analytics-data/?days=${period}`);
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Helpers ──
  const fillDailyData = (sparse, days) => {
    const map = {};
    (sparse || []).forEach(d => { map[d.date] = d.count; });
    const result = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      result.push({ date: label, rawDate: key, count: map[key] || 0 });
    }
    return result;
  };

  const mergeTimeSeries = (viewsArr, savesArr) => {
    return viewsArr.map((v, i) => ({
      date: v.date,
      Views: v.count,
      Saves: savesArr[i]?.count || 0,
    }));
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading analytics...</span>
      </div>
    );
  }

  const totals = analytics?.totals || {};
  const dailyViews = fillDailyData(analytics?.daily_product_views, period);
  const dailyBrandViews = fillDailyData(analytics?.daily_brand_views, period);
  const dailySaves = fillDailyData(analytics?.daily_product_saves, period);
  const engagementTimeSeries = mergeTimeSeries(dailyViews, dailySaves);
  const brandViewTimeSeries = dailyBrandViews.map(d => ({ date: d.date, 'Brand Views': d.count }));
  const topProducts = analytics?.top_products || [];
  const categoryPerformance = analytics?.category_performance || [];
  const brandRanking = analytics?.brand_ranking || {};
  const growthRates = analytics?.growth_rates || {};
  const industryAverages = analytics?.industry_averages || {};
  const productAnalytics = analytics?.product_analytics || {};
  const hourlyActivity = analytics?.hourly_activity || [];
  
  // Calculate conversion metrics
  const totalProductViews = totals.product_views || 0;
  const totalProductSaves = totals.product_saves || 0;
  const totalBrandViews = totals.brand_page_views || 0;
  const totalBrandSaves = totals.brand_saves || 0;
  
  const saveRate = totalProductViews > 0 ? ((totalProductSaves / totalProductViews) * 100).toFixed(1) : 0;
  const followRate = totalBrandViews > 0 ? ((totalBrandSaves / totalBrandViews) * 100).toFixed(1) : 0;
  
  // Engagement Quality: weighted composite of save rate (60%) + follow rate (40%)
  const engagementQuality = (
    (parseFloat(saveRate) * 0.6) + (parseFloat(followRate) * 0.4)
  ).toFixed(1);
  
  // Calculate growth rates if not provided
  const weekOverWeek = growthRates.week_over_week || { views_change: 0, saves_change: 0, follows_change: 0 };
  const monthOverMonth = growthRates.month_over_month || { views_change: 0, saves_change: 0, follows_change: 0 };

  return (
    <div>
      {/* Header */}
      <div className="bp-header-row" style={styles.headerRow}>
        <div>
          <div style={styles.label}>E N G A G E M E N T</div>
          <br />
          <p style={styles.subtitle}>How users are engaging with your brand and products</p>
        </div>
        <div className="bp-period-selector" style={styles.periodSelector}>
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              style={{
                ...styles.periodBtn,
                ...(period === opt.value ? styles.periodBtnActive : {}),
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Engagement Metrics */}
      <div className="bp-metrics-grid" style={styles.metricsGrid}>
        <div className="bp-metric-card" style={{ ...styles.metricCard, background: '#171717' }}>
          <Target style={styles.metricIcon} />
          <h3 style={styles.metricTitle}>Save Rate</h3>
          <div className="bp-metric-value" style={styles.metricValue}>{saveRate}%</div>
          <div style={styles.metricSubtitle}>Purchase intent indicator</div>
        </div>
        <div className="bp-metric-card" style={{ ...styles.metricCard, background: '#27272a' }}>
          <Users style={styles.metricIcon} />
          <h3 style={styles.metricTitle}>Follow Rate</h3>
          <div className="bp-metric-value" style={styles.metricValue}>{followRate}%</div>
          <div style={styles.metricSubtitle}>Brand appeal score</div>
        </div>
        <div className="bp-metric-card" style={{ ...styles.metricCard, background: '#3f3f46' }}>
          <Activity style={styles.metricIcon} />
          <h3 style={styles.metricTitle}>Engagement Quality</h3>
          <div className="bp-metric-value" style={styles.metricValue}>{engagementQuality}%</div>
          <div style={styles.metricSubtitle}>Overall interaction quality</div>
        </div>
        <div className="bp-metric-card" style={{ ...styles.metricCard, background: '#52525b' }}>
          <Trophy style={styles.metricIcon} />
          <h3 style={styles.metricTitle}>Brand Rank</h3>
          <div className="bp-metric-value" style={styles.metricValue}>#{brandRanking.rank || 'N/A'}</div>
          <div style={styles.metricSubtitle}>Out of {brandRanking.total_brands || 0} brands</div>
        </div>
      </div>

      {/* Product Views & Saves Time Series */}
      <div className="bp-chart-card" style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div style={styles.label}>P R O D U C T &nbsp; E N G A G E M E N T</div>
          <h3 style={styles.chartTitle}>Product Views & Saves Over Time</h3>
        </div>
        {engagementTimeSeries.some(d => d.Views > 0 || d.Saves > 0) ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={engagementTimeSeries}>
              <defs>
                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#171717" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#171717" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorSaves" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(period / 7) - 1)} />
              <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '13px' }} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} formatter={(value) => <span style={{ color: '#71717a', fontSize: '13px' }}>{value}</span>} />
              <Area type="monotone" dataKey="Views" stroke="#171717" fill="url(#colorViews)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="Saves" stroke="#ef4444" fill="url(#colorSaves)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.emptyChart}>
            <Eye style={{ width: '40px', height: '40px', color: '#d4d4d4', marginBottom: '12px' }} />
            <p>No engagement data yet for this period</p>
            <p style={{ fontSize: '12px', color: '#a1a1aa' }}>As users view and save your products, data will appear here</p>
          </div>
        )}
      </div>

      {/* Brand Page Views Time Series */}
      <div className="bp-chart-card" style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div style={styles.label}>B R A N D &nbsp; P A G E</div>
          <h3 style={styles.chartTitle}>Brand Page Views Over Time</h3>
        </div>
        {brandViewTimeSeries.some(d => d['Brand Views'] > 0) ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={brandViewTimeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(period / 7) - 1)} />
              <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '13px' }} />
              <Bar dataKey="Brand Views" fill="#171717" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.emptyChart}>
            <Users style={{ width: '40px', height: '40px', color: '#d4d4d4', marginBottom: '12px' }} />
            <p>No brand page views yet for this period</p>
          </div>
        )}
      </div>

      {/* Top Performing Products */}
      {topProducts.length > 0 && (
        <div className="bp-chart-card" style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.label}>T O P &nbsp; P R O D U C T S</div>
            <h3 style={styles.chartTitle}>Best Performing Products</h3>
          </div>
          <div style={styles.tableWrap}>
            <table className="bp-table" style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Product</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Views</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Saves</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Save Rate</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((p, i) => (
                  <tr key={p.product_id} style={i % 2 === 0 ? {} : { background: '#fafafa' }}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {p.image ? (
                          <img src={p.image} alt="" style={styles.thumbImg} />
                        ) : (
                          <div style={styles.thumbPlaceholder} />
                        )}
                        <span className="bp-table-product-name" style={{ fontSize: '14px', fontWeight: '500', color: '#171717', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{p.title}</span>
                      </div>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>{p.views}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#ef4444' }}>{p.saves}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: p.conversion_rate > 10 ? '#f0fdf4' : p.conversion_rate > 0 ? '#fffbeb' : '#f5f5f5',
                        color: p.conversion_rate > 10 ? '#16a34a' : p.conversion_rate > 0 ? '#d97706' : '#a1a1aa',
                      }}>
                        {p.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Performance Benchmarking */}
      <div className="bp-two-column" style={styles.twoColumn}>
        {/* Category Performance */}
        <div className="bp-card" style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>P E R F O R M A N C E</div>
            <h3 style={styles.cardTitle}>Category Performance</h3>
          </div>
          {categoryPerformance.length > 0 ? (
            <div style={styles.sourceList}>
              {categoryPerformance.map((cat, index) => {
                const performance = cat.performance_vs_avg;
                const color = performance > 120 ? '#22c55e' : performance > 80 ? '#f59e0b' : '#ef4444';
                return (
                  <div key={cat.category}>
                    <div style={styles.sourceItem}>
                      <div style={{
                        ...styles.sourceIcon,
                        background: `${color}20`,
                        color: color
                      }}>
                        <BarChart3 style={{ width: '18px', height: '18px' }} />
                      </div>
                      <div style={styles.sourceInfo}>
                        <span style={styles.sourceName}>{cat.category}</span>
                        <span style={styles.sourceCount}>{performance}% of average</span>
                      </div>
                      <span style={{ ...styles.sourcePercent, color }}>
                        {performance > 100 ? '+' : ''}{performance - 100}%
                      </span>
                    </div>
                    <div style={styles.progressBar}>
                      <div style={{
                        ...styles.progressFill,
                        width: `${Math.min(performance, 150) / 1.5}%`,
                        background: color
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.emptyCard}>
              <p>No category data yet</p>
            </div>
          )}
        </div>

        {/* Growth Rates */}
        <div className="bp-card" style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>G R O W T H</div>
            <h3 style={styles.cardTitle}>Growth Rates</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#171717' }}>Week over Week</span>
                <span style={{ fontSize: '13px', color: '#71717a' }}>Last 7 days</span>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: weekOverWeek.views_change >= 0 ? '#22c55e' : '#ef4444' }}>
                    {weekOverWeek.views_change >= 0 ? '+' : ''}{weekOverWeek.views_change}%
                  </div>
                  <div style={{ fontSize: '11px', color: '#a1a1aa' }}>Views</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: weekOverWeek.saves_change >= 0 ? '#22c55e' : '#ef4444' }}>
                    {weekOverWeek.saves_change >= 0 ? '+' : ''}{weekOverWeek.saves_change}%
                  </div>
                  <div style={{ fontSize: '11px', color: '#a1a1aa' }}>Saves</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: weekOverWeek.follows_change >= 0 ? '#22c55e' : '#ef4444' }}>
                    {weekOverWeek.follows_change >= 0 ? '+' : ''}{weekOverWeek.follows_change}%
                  </div>
                  <div style={{ fontSize: '11px', color: '#a1a1aa' }}>Follows</div>
                </div>
              </div>
            </div>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#171717' }}>Month over Month</span>
                <span style={{ fontSize: '13px', color: '#71717a' }}>Last 30 days</span>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: monthOverMonth.views_change >= 0 ? '#22c55e' : '#ef4444' }}>
                    {monthOverMonth.views_change >= 0 ? '+' : ''}{monthOverMonth.views_change}%
                  </div>
                  <div style={{ fontSize: '11px', color: '#a1a1aa' }}>Views</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: monthOverMonth.saves_change >= 0 ? '#22c55e' : '#ef4444' }}>
                    {monthOverMonth.saves_change >= 0 ? '+' : ''}{monthOverMonth.saves_change}%
                  </div>
                  <div style={{ fontSize: '11px', color: '#a1a1aa' }}>Saves</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: monthOverMonth.follows_change >= 0 ? '#22c55e' : '#ef4444' }}>
                    {monthOverMonth.follows_change >= 0 ? '+' : ''}{monthOverMonth.follows_change}%
                  </div>
                  <div style={{ fontSize: '11px', color: '#a1a1aa' }}>Follows</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Peak Activity Times */}
      <div className="bp-chart-card" style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div style={styles.label}>A C T I V I T Y &nbsp; P A T T E R N S</div>
          <h3 style={styles.chartTitle}>Peak Activity Times</h3>
        </div>
        {hourlyActivity.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="hour" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '13px' }} />
              <Bar dataKey="engagement" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.emptyChart}>
            <Clock style={{ width: '40px', height: '40px', color: '#d4d4d4', marginBottom: '12px' }} />
            <p>No activity data yet</p>
          </div>
        )}
      </div>

      {/* Product Performance Analytics */}
      <div className="bp-two-column" style={styles.twoColumn}>
        {/* Price Point Analysis */}
        <div className="bp-card" style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>P R I C I N G</div>
            <h3 style={styles.cardTitle}>Price Point Performance</h3>
          </div>
          {productAnalytics.price_ranges ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {productAnalytics.price_ranges.map((range, index) => {
                const avgEngagement = range.avg_engagement_rate || 0;
                const color = avgEngagement > 5 ? '#22c55e' : avgEngagement > 2 ? '#f59e0b' : '#ef4444';
                return (
                  <div key={range.range} style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#171717' }}>{range.range}</span>
                      <span style={{ fontSize: '13px', color: color, fontWeight: '600' }}>{avgEngagement}% engagement</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#71717a' }}>{range.product_count} products</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.emptyCard}>
              <p>No price data yet</p>
            </div>
          )}
        </div>

        {/* Seasonal Trends */}
        <div className="bp-card" style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>S E A S O N A L</div>
            <h3 style={styles.cardTitle}>Seasonal Performance</h3>
          </div>
          {productAnalytics.seasonal_trends ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {productAnalytics.seasonal_trends.map((season, index) => {
                const performance = season.performance_vs_avg;
                const color = performance > 120 ? '#22c55e' : performance > 80 ? '#f59e0b' : '#ef4444';
                return (
                  <div key={season.season} style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#171717' }}>{season.season}</span>
                      <span style={{ fontSize: '13px', color: color, fontWeight: '600' }}>
                        {performance > 100 ? '+' : ''}{performance - 100}% vs avg
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.emptyCard}>
              <p>No seasonal data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Industry Averages */}
      <div className="bp-chart-card" style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div style={styles.label}>B E N C H M A R K</div>
          <h3 style={styles.chartTitle}>Industry Averages Comparison</h3>
        </div>
        {industryAverages ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#171717', marginBottom: '4px' }}>
                {industryAverages.avg_save_rate || 'N/A'}%
              </div>
              <div style={{ fontSize: '12px', color: '#71717a' }}>Industry Save Rate</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: saveRate > (industryAverages.avg_save_rate || 0) ? '#22c55e' : '#ef4444', marginTop: '4px' }}>
                You: {saveRate}%
              </div>
            </div>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#171717', marginBottom: '4px' }}>
                {industryAverages.avg_follow_rate || 'N/A'}%
              </div>
              <div style={{ fontSize: '12px', color: '#71717a' }}>Industry Follow Rate</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: followRate > (industryAverages.avg_follow_rate || 0) ? '#22c55e' : '#ef4444', marginTop: '4px' }}>
                You: {followRate}%
              </div>
            </div>
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#171717', marginBottom: '4px' }}>
                {industryAverages.avg_engagement_rate || 'N/A'}%
              </div>
              <div style={{ fontSize: '12px', color: '#71717a' }}>Industry Engagement</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: engagementQuality > (industryAverages.avg_engagement_rate || 0) ? '#22c55e' : '#ef4444', marginTop: '4px' }}>
                You: {engagementQuality}%
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.emptyChart}>
            <BarChart3 style={{ width: '40px', height: '40px', color: '#d4d4d4', marginBottom: '12px' }} />
            <p>No industry data yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#71717a',
    margin: 0,
  },
  periodSelector: {
    display: 'flex',
    background: '#f5f5f5',
    borderRadius: '8px',
    padding: '3px',
    gap: '2px',
  },
  periodBtn: {
    padding: '10px 18px',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: '#71717a',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  periodBtnActive: {
    background: '#ffffff',
    color: '#171717',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    fontWeight: '600',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '32px',
  },
  metricCard: {
    padding: '24px',
    borderRadius: '16px',
    color: '#ffffff',
  },
  metricIcon: {
    width: '24px',
    height: '24px',
    marginBottom: '16px',
    color: '#ffffff',
    stroke: '#ffffff',
    opacity: 0.8,
  },
  metricTitle: {
    fontSize: '13px',
    fontWeight: '500',
    margin: '0 0 8px 0',
    opacity: 0.7,
    color: '#ffffff',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: '500',
    marginBottom: '4px',
    color: '#ffffff',
  },
  metricSubtitle: {
    fontSize: '13px',
    opacity: 0.6,
    color: '#ffffff',
  },
  chartCard: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
  },
  chartHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  chartTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  emptyChart: {
    height: '280px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#a1a1aa',
    fontSize: '14px',
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    marginBottom: '24px',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
  },
  cardHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  progressBar: {
    height: '6px',
    background: '#f5f5f5',
    borderRadius: '3px',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    background: '#171717',
    borderRadius: '3px',
    transition: 'width 0.5s ease',
  },
  sourceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '6px',
  },
  sourceIcon: {
    width: '36px',
    height: '36px',
    background: '#f5f5f5',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#71717a',
  },
  sourceInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  sourceName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
  },
  sourceCount: {
    fontSize: '12px',
    color: '#71717a',
  },
  sourcePercent: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#171717',
  },
  emptyCard: {
    height: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#a1a1aa',
    fontSize: '14px',
    textAlign: 'center',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '11px',
    letterSpacing: '0.08em',
    color: '#a1a1aa',
    fontWeight: '600',
    textTransform: 'uppercase',
    borderBottom: '1px solid #e5e5e5',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#171717',
    borderBottom: '1px solid #f5f5f5',
  },
  thumbImg: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
    background: '#f5f5f5',
  },
  thumbPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: '#f5f5f5',
  },
};

export default BrandAnalytics;
